import { isAxiosError } from "axios";

/** 한글이 하나라도 있으면 사용자에게 보여 주려고 쓴 문구로 본다. */
const HAS_HANGUL = /[가-힣]/;

/**
 * 서버 오류를 사용자가 조치할 수 있는 한국어 문장으로 바꾼다.
 *
 * 백엔드는 조치 가능한 사유를 한국어 `detail` 로 준다(예: 409 "이 시료는 이미
 * 분석이 진행 중입니다 (분석 #12)"). 그걸 버리고 "실패했습니다" 로 덮으면
 * 사용자는 무엇을 해야 할지 알 수 없다 — 특히 동시 실행 차단(SVD-212-09)은
 * 사유를 봐야만 진행 중인 분석을 찾아갈 수 있다.
 *
 * 그렇다고 `detail` 을 무조건 그대로 쓰면 반대 문제가 생긴다. 서버에는
 * "Could not validate credentials" / "Sample not found" / "Internal Server
 * Error" 같은 **내부 영어 문구**도 있고, 그걸 한국어 화면에 그대로 띄우는 것은
 * 사용자에게 암호문을 보여 주는 것이다(코딩 컨벤션: 사용자 노출 문구는 한국어).
 * 그래서 **한글이 있는 detail 만 통과**시키고 나머지는 상태 코드별 한국어
 * 문장으로 바꾼다.
 *
 * FastAPI 의 422 는 `detail` 이 배열이라 그대로 쓰면 "[object Object]" 가
 * 되고, "잠시 후 다시 시도하세요" 라고 안내하면 틀린 조치다(검증 오류는
 * 기다린다고 낫지 않는다) — 별도 문장으로 처리한다.
 */
export function apiErrorDetail(err: unknown, fallback: string): string {
  if (!isAxiosError(err)) return fallback;

  const status = err.response?.status;
  const detail = err.response?.data?.detail;

  if (Array.isArray(detail)) {
    return "입력값이 올바르지 않습니다. 입력 항목을 확인해 주세요.";
  }
  if (typeof detail === "string" && HAS_HANGUL.test(detail)) {
    return detail.trim();
  }

  switch (status) {
    case 401:
      // 인터셉터가 토큰을 갱신했을 수 있다 — 한 번 더 누르면 되는 상황이다.
      return "로그인 세션이 갱신되었습니다. 다시 시도해 주세요.";
    case 403:
      return "이 작업을 수행할 권한이 없습니다.";
    case 404:
      return "대상을 찾을 수 없습니다. 목록을 새로 고친 뒤 다시 시도하세요.";
    case 422:
      return "입력값이 올바르지 않습니다. 입력 항목을 확인해 주세요.";
    default:
      return fallback;
  }
}

async function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * 파일 내려받기(`responseType: "blob"`) 오류용 {@link apiErrorDetail}.
 *
 * 그런 요청은 오류 응답 본문도 Blob 으로 온다 — `data.detail` 이 없어 서버의
 * 한국어 사유(예: 미완료 분석 400 `REPORT_ANALYSIS_NOT_COMPLETED`)가 사라진다.
 * 본문을 JSON 으로 읽어 같은 규칙으로 판정한다. 원 오류 객체는 바꾸지 않는다.
 */
export async function blobApiErrorDetail(
  err: unknown,
  fallback: string,
): Promise<string> {
  if (!isAxiosError(err) || !(err.response?.data instanceof Blob)) {
    return apiErrorDetail(err, fallback);
  }
  let data: unknown;
  try {
    data = JSON.parse(await readBlobText(err.response.data));
  } catch {
    data = undefined; // JSON 이 아니면(게이트웨이 HTML 등) 상태 코드로 판정
  }
  const readable = Object.assign(
    Object.create(Object.getPrototypeOf(err)),
    err,
    { response: { ...err.response, data } },
  );
  return apiErrorDetail(readable, fallback);
}
